import {
  CartCompletenessStatus,
  CaseResponseDto,
  CaseStatus,
  PreferenceCardResponseDto,
  SelectedCartDto,
  SelectedCartLocationDto,
} from '@peritasai/types';
import { BuildChecklistEntity } from '../build-cart/build-checklist.entity';
import { PreferenceCardEntity } from './preference-card.entity';

/**
 * Surgical case entity representing the core business object
 * This entity contains business logic and validation
 */
export class SurgicalCaseEntity {
  public readonly id: string; // UUID
  public readonly caseHospitalId: string; // Readable ID (e.g. CASE-061)
  public readonly surgeon: string;
  public readonly caseType: string;
  public readonly room: string;
  public readonly floor: string | null;
  public readonly departmentOrUnit: string | null;
  public readonly locationName: string | null;
  public readonly locationId: string | null;
  public readonly dateTime: Date;
  public readonly duration: number;
  public readonly caseActiveStatus: boolean;
  public readonly cartID: string;
  public readonly cartReferenceNumber: string;
  public readonly cartLocation: string; //TODO: We need to pull cart asset (if assigned) and display the location.name
  public readonly selectedCart: SelectedCartDto | null;
  public readonly traysRequired: number;
  public readonly traysLoaded: number;
  public readonly traysReserved: number;
  public readonly traysMissing: number;
  public readonly instrumentsSuppliesRequired: number;
  public readonly instrumentsSuppliesLoaded: number;
  public readonly buildChecklist?: BuildChecklistEntity;
  public readonly cartCompletenessStatus: string;
  public readonly checkedOutStatus: boolean;
  public readonly preferenceCard?: PreferenceCardEntity;
  public readonly verifiedAt: Date | null;
  public readonly verifiedByName: string | null;

  constructor(
    dto: CaseResponseDto,
    preferenceCardDto?: PreferenceCardResponseDto,
    cartLocation?: string,
    cartReferenceNumber?: string
  ) {
    // Map CaseResponseDto fields to entity fields
    this.id = dto.id; // The UUID from the backend
    this.caseHospitalId = dto.caseId; // The human-readable ID (e.g. "CASE-061")
    this.surgeon = dto.surgeonName || 'Unknown';
    this.caseType = dto.caseTypeDisplayName || 'Unknown';
    this.room = dto.room || 'Unknown';
    this.floor = dto.location?.floor ?? null;
    this.departmentOrUnit = dto.location?.departmentOrUnit || null;
    this.locationName = dto.location?.name ?? null;
    this.locationId = dto.location?.id ?? null;

    // Convert scheduledDateTime to Date if it's a string
    const scheduledDateTime =
      typeof dto.scheduledDateTime === 'string'
        ? new Date(dto.scheduledDateTime)
        : dto.scheduledDateTime;
    this.dateTime =
      scheduledDateTime instanceof Date ? scheduledDateTime : new Date();

    this.duration = dto.duration ?? 0;

    // Map CaseStatus enum to caseActiveStatus boolean
    this.caseActiveStatus =
      dto.status !== CaseStatus.FINISHED && dto.status !== CaseStatus.CANCELED;

    // Extract cart-related fields from buildChecklist
    this.buildChecklist = dto.buildChecklist
      ? new BuildChecklistEntity(dto.buildChecklist)
      : undefined;
    this.cartID = dto.selectedCart?.assetId ?? '';
    this.cartReferenceNumber = dto.selectedCart?.referenceNumber ?? '';
    this.selectedCart = dto.selectedCart ?? null;
    this.cartLocation =
      SurgicalCaseEntity.getLocationFromSelectedCart(
        dto.selectedCart?.location
      ) ??
      cartLocation ??
      'Unknown';
    this.traysRequired = dto.buildChecklist?.traysTotal || 0;
    this.traysLoaded = dto.buildChecklist?.traysAllocated || 0;
    this.traysReserved = dto.buildChecklist?.traysReserved || 0;
    this.traysMissing = dto.buildChecklist?.traysMissing || 0;

    // Calculate instruments and supplies counts
    const { required, loaded } = this.calculateInstrumentsAndSuppliesCounts(
      this.buildChecklist
    );
    this.instrumentsSuppliesRequired = required;
    this.instrumentsSuppliesLoaded = loaded;

    // Use BE cartCompletenessStatus when present, else fallback to 'Not Started'
    this.cartCompletenessStatus =
      dto.cartCompletenessStatus != null && dto.cartCompletenessStatus !== ''
        ? (dto.cartCompletenessStatus as string)
        : 'Not Started';

    this.checkedOutStatus = dto.dispatchStatus || false;
    this.preferenceCard = preferenceCardDto
      ? new PreferenceCardEntity(preferenceCardDto)
      : undefined;
    this.verifiedAt = dto.verifiedAt
      ? typeof dto.verifiedAt === 'string'
        ? new Date(dto.verifiedAt)
        : dto.verifiedAt
      : null;
    this.verifiedByName = dto.verifiedByName ?? null;
  }

  /**
   * Derive location string from selectedCart location
   */
  private static getLocationFromSelectedCart(
    loc?: SelectedCartLocationDto | null
  ): string | undefined {
    if (!loc) return undefined;
    if (loc.locationDisplayName) return loc.locationDisplayName;
    const parts = [loc.departmentOrUnit, loc.name, loc.floor].filter(
      Boolean
    ) as string[];
    return parts.length > 0 ? parts.join(', ') : undefined;
  }

  /**
   * Calculate required and loaded counts for instruments and supplies
   */
  private calculateInstrumentsAndSuppliesCounts(
    checklist?: BuildChecklistEntity
  ): { required: number; loaded: number } {
    if (!checklist) {
      return { required: 0, loaded: 0 };
    }

    let required = 0;
    let loaded = 0;

    // 1. Single Packed Instruments (Untracked)
    checklist.getSinglePackedInstruments().forEach((group) => {
      required += group.totalCount;
      loaded += group.allocatedCount;
    });

    // 2. Untracked Instruments
    checklist.getUntrackedInstruments().forEach((group) => {
      required += group.totalCount;
      loaded += group.allocatedCount;
    });

    // 3. Supplies (Untracked)
    checklist.getSupplies().forEach((group) => {
      required += group.totalCount;
      loaded += group.allocatedCount;
    });

    return { required, loaded };
  }

  /**
   * Check if the case is currently in progress
   */
  public isInProgress(): boolean {
    return this.cartCompletenessStatus === 'In Progress';
  }

  /**
   * Check if the case's cart has arrived at the OR (fully complete) — a
   * merely dispatched cart is still en route, not yet complete.
   */
  public isCompleted(): boolean {
    return this.cartCompletenessStatus === 'Arrived';
  }

  /**
   * Check if the case has not been started
   */
  public isNotStarted(): boolean {
    return this.cartCompletenessStatus === 'Not Started';
  }

  /**
   * Check if the case is incomplete
   */
  public isIncomplete(): boolean {
    return this.cartCompletenessStatus === 'Incomplete';
  }

  /**
   * Get the case duration in hours
   */
  public getDurationInHours(): number {
    return this.duration / 60;
  }

  /**
   * Check if the case is scheduled for today
   */
  public isScheduledForToday(): boolean {
    const today = new Date();
    return this.dateTime.toDateString() === today.toDateString();
  }

  /**
   * Get a formatted date string
   */
  public getFormattedDate(): string {
    return this.dateTime.toLocaleDateString();
  }

  /**
   * Get a formatted time string
   */
  public getFormattedTime(): string {
    return this.dateTime.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }

  /**
   * Get cart fill percentage (0-100)
   */
  public getCartFillPercentage(): number {
    if (this.traysRequired === 0) return 0;
    return Math.round((this.traysLoaded / this.traysRequired) * 100);
  }

  /**
   * Get formatted cart location string
   */
  public getFormattedCartLocation(): string {
    return `${this.cartLocation}`;
  }

  /**
   * Get formatted start time string in MM/dd/YY \n h:mm format
   */
  public getFormattedStartTime(): string {
    const date = this.dateTime.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    });
    const time = this.dateTime
      .toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
      .replace(/\s*(AM|PM)$/i, (_, meridiem: string) => meridiem.toLowerCase());
    return `${date}, ${time}`;
  }

  /**
   * Convert back to DTO for API communication
   * Returns a simplified structure compatible with the old SurgicalCaseDTO
   * @deprecated Consider using CaseResponseDto directly instead
   */
  public toDTO(): {
    id: string;
    caseHospitalId: string;
    surgeon: string;
    caseType: string;
    room: string;
    dateTime: Date;
    duration: number;
    caseActiveStatus: boolean;
    cartID: string;
    cartLocation: string;
    traysRequired: number;
    traysLoaded: number;
    cartCompletenessStatus: CartCompletenessStatus;
    checkedOutStatus: boolean;
  } {
    return {
      id: this.id,
      caseHospitalId: this.caseHospitalId,
      surgeon: this.surgeon,
      caseType: this.caseType,
      room: this.room,
      dateTime: this.dateTime,
      duration: this.duration,
      caseActiveStatus: this.caseActiveStatus,
      cartID: this.cartID,
      cartLocation: this.cartLocation,
      traysRequired: this.traysRequired,
      traysLoaded: this.traysLoaded,
      cartCompletenessStatus: this
        .cartCompletenessStatus as CartCompletenessStatus,
      checkedOutStatus: this.checkedOutStatus,
    };
  }
}
